import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import {
  Bold, Italic, Strikethrough, List, ListOrdered, Link as LinkIcon, Image as ImageIcon,
  Undo, Redo, Code2
} from 'lucide-react';

import { Button } from '../ui/button';

import { MediaLibraryBrowser } from './shared';

// Custom Image extension that preserves all HTML attributes
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      // Preserve style attribute
      style: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
      // Preserve class attribute
      class: {
        default: null,
        parseHTML: element => element.getAttribute('class'),
        renderHTML: attributes => {
          if (!attributes.class) return {};
          return { class: attributes.class };
        },
      },
      // Preserve width attribute
      width: {
        default: null,
        parseHTML: element => element.getAttribute('width'),
        renderHTML: attributes => {
          if (!attributes.width) return {};
          return { width: attributes.width };
        },
      },
      // Preserve height attribute
      height: {
        default: null,
        parseHTML: element => element.getAttribute('height'),
        renderHTML: attributes => {
          if (!attributes.height) return {};
          return { height: attributes.height };
        },
      },
    };
  },
});

const MenuBar = ({ editor, onImageUpload, context }) => {
  const [showMediaBrowser, setShowMediaBrowser] = useState(false);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const handleImageSelect = (url) => {
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
    setShowMediaBrowser(false);
  };

  return (
    <>
      <MediaLibraryBrowser
        isOpen={showMediaBrowser}
        onClose={() => setShowMediaBrowser(false)}
        onSelect={handleImageSelect}
        context={context || 'content'}
      />
    <div className="border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-2 flex flex-wrap gap-1">
      <Button
        size="sm"
        variant={editor.isActive('bold') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleBold().run()}
        type="button"
        className="h-8 w-8 p-0"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('italic') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        type="button"
        className="h-8 w-8 p-0"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('strike') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        type="button"
        className="h-8 w-8 p-0"
      >
        <Strikethrough className="h-4 w-4" />
      </Button>
      
      <div className="w-px h-8 bg-gray-300 mx-1" />
      
      <Button
        size="sm"
        variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        type="button"
        className="h-8 px-2 text-xs"
      >
        H1
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        type="button"
        className="h-8 px-2 text-xs"
      >
        H2
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        type="button"
        className="h-8 px-2 text-xs"
      >
        H3
      </Button>
      
      <div className="w-px h-8 bg-gray-300 mx-1" />
      
      <Button
        size="sm"
        variant={editor.isActive('bulletList') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        type="button"
        className="h-8 w-8 p-0"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('orderedList') ? 'default' : 'outline'}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        type="button"
        className="h-8 w-8 p-0"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      
      <div className="w-px h-8 bg-gray-300 mx-1" />
      
      <Button
        size="sm"
        variant="outline"
        onClick={addLink}
        type="button"
        className="h-8 w-8 p-0"
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowMediaBrowser(true)}
        type="button"
        className="h-8 w-8 p-0"
        title="Insert image"
      >
        <ImageIcon className="h-4 w-4" />
      </Button>

      <div className="w-px h-8 bg-gray-300 mx-1" />

      <Button
        size="sm"
        variant="outline"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        type="button"
        className="h-8 w-8 p-0"
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        type="button"
        className="h-8 w-8 p-0"
      >
        <Redo className="h-4 w-4" />
      </Button>
    </div>
    </>
  );
};

const WysiwygEditor = ({ value, onChange, placeholder, onImageUpload, onOpenCodeEditor, context }) => {
  // Track whether we're updating content externally to prevent onChange feedback loop
  const isExternalUpdate = React.useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      CustomImage, // Use custom image extension that preserves attributes
    ],
    content: value,
    onUpdate: ({ editor }) => {
      // Don't fire onChange when content is set externally (e.g., from version restore)
      // This prevents TipTap from corrupting complex HTML that it doesn't fully support
      if (isExternalUpdate.current) {
        return;
      }
      onChange(editor.getHTML());
    },
  });

  // Update editor content when value prop changes
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Mark as external update to prevent onChange from firing
      isExternalUpdate.current = true;
      editor.commands.setContent(value);
      // Reset flag after a tick to allow normal editing to trigger onChange
      setTimeout(() => {
        isExternalUpdate.current = false;
      }, 0);
    }
  }, [value, editor]);

  return (
    <div className="border rounded-lg overflow-hidden bg-background">
      <MenuBar editor={editor} onImageUpload={onImageUpload} context={context} />
      <EditorContent
        editor={editor}
        className="prose max-w-none p-4 min-h-[300px] focus:outline-none"
      />

      {/* Advanced Editor Button */}
      {onOpenCodeEditor && (
        <div className="border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenCodeEditor}
            className="flex items-center space-x-2"
          >
            <Code2 className="h-4 w-4" />
            <span>Advanced Editor (Code View)</span>
          </Button>
        </div>
      )}
      <style jsx global>{`
        .ProseMirror {
          min-height: 300px;
          outline: none;
        }
        .ProseMirror p {
          margin: 0.5rem 0;
        }
        .ProseMirror h1 {
          font-size: 2rem;
          font-weight: bold;
          margin: 1rem 0 0.5rem 0;
        }
        .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: bold;
          margin: 1rem 0 0.5rem 0;
        }
        .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: bold;
          margin: 1rem 0 0.5rem 0;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .ProseMirror li {
          margin: 0.25rem 0;
        }
        .ProseMirror a {
          color: #dc2626;
          text-decoration: underline;
        }
        .ProseMirror img:not([style]) {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
        /* Images WITH inline styles: let their styles take precedence */
        .ProseMirror img[style] {
          height: auto;
        }
        .ProseMirror strong {
          font-weight: bold;
        }
        .ProseMirror em {
          font-style: italic;
        }
      `}</style>
    </div>
  );
};

export default WysiwygEditor;
